/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AuthoringRegion } from 'src/core/types/regionTypes';
import { getEnvironment, isProductionEnvironment, isStagingEnvironment } from 'src/core/utils/api/environment';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { allowedCsAccountRegions } from 'src/core/utils/regionUtils';

/**
 * Gets the current region based on the current authoring resource
 * if the migration deprecation toggle is on and the default
 * region if the migration deprecation toggle is off.
 *
 * @param currentAzureCsAccount The current authoring resource.
 */
export const getOrDefaultRegionFromResource = (currentAzureCsAccount: AzureCsAccountStore): AuthoringRegion => {
    if (currentAzureCsAccount) {
        return <AuthoringRegion>currentAzureCsAccount.region;
    }
    return getEnvironment().defaultRegion;
};

/**
 * Takes azure resource path and returns the resource name.
 */
export const getResourcePathData = (path: string) => {
    const d = path.split('/');

    return { subscriptionId: d[2], resourceGroupName: d[4], name: d[8] };
};
/// ///////////////////////////// FILTER PREDICATES ////////////////////////////////

/**
 * Returns whether the resource could be viewed
 * inside a specific app or not.
 */
export const isPredictionResourceAllowedInAuthoringRegion = (currentResourceRegion: AuthoringRegion) => (csAccount: AzureCsAccountStore) =>
    allowedCsAccountRegions.get(currentResourceRegion)?.some(r => r === csAccount.region);

export const isAuthoringResource = (csAccount: AzureCsAccountStore) => csAccount.kind === 'LUIS.Authoring';

export const isPredictionResource = (csAccount: AzureCsAccountStore) => csAccount.kind === 'LUIS' || csAccount.kind === 'CognitiveServices';

export const isTextAnalyticsResource = (csAccount: AzureCsAccountStore) =>
    csAccount.kind === 'TextAnalytics' && (csAccount.sku.code === 'F0' || csAccount.sku.code === 'S');

export const isCognitiveServicesResource = (csAccount: AzureCsAccountStore) =>
    csAccount.kind === 'CognitiveServices' && csAccount.sku.code === 'S0';

export const isCustomTextResource = (csAccount: AzureCsAccountStore) =>
    csAccount.kind === 'TextAnalytics' &&
    LuisConstants.LanguageStudio.AllowedSkuCodes.some(sku => sku === csAccount.sku.code) &&
    LuisConstants.CustomText.PrivatePreviewAllowedRegions.some(region => region === csAccount.region);

export const isSummarizationResource = (csAccount: AzureCsAccountStore) =>
    csAccount.kind === 'TextAnalytics' &&
    LuisConstants.LanguageStudio.AllowedSkuCodes.some(sku => sku === csAccount.sku.code) &&
    ((isProductionEnvironment() && LuisConstants.Summarization.SupportedRegions.some(region => region === csAccount.region)) ||
        ((!isProductionEnvironment() || isStagingEnvironment()) &&
            LuisConstants.Summarization.SupportedRegions.concat('westus2').some(region => region === csAccount.region)));

export const isCustomHealthcareResource = (csAccount: AzureCsAccountStore) =>
    csAccount.kind === 'TextAnalytics' &&
    LuisConstants.LanguageStudio.AllowedSkuCodes.some(sku => sku === csAccount.sku.code) &&
    LuisConstants.Healthcare.SupportedRegions.some(region => region === csAccount.region);

export const isCluResource = (csAccount: AzureCsAccountStore) =>
    csAccount.kind === 'TextAnalytics' &&
    LuisConstants.LanguageStudio.AllowedSkuCodes.some(sku => sku === csAccount.sku.code) &&
    LuisConstants.Clu.PrivatePreviewAllowedRegions.some(region => region === csAccount.region);

/**
 * Excludes an account if it already exists in the
 * given array of accounts.
 *
 * @param accounts The accounts to search in.
 */
export const excludeAccountIfExists = (accounts: AzureCsAccountStore[]) => (account: AzureCsAccountStore) =>
    accounts.every(a => a.path !== account.path);

/// ///////////////////////////// END FILTER PREDICATES ////////////////////////////////

export const renderPredictionCsAccounts = (csAccounts: AzureCsAccountStore[], region: AuthoringRegion): IDropdownOption[] => {
    const allowedRegions = allowedCsAccountRegions.get(region);

    return csAccounts
        .filter(a => a.kind !== 'LUIS.Authoring')
        .map<IDropdownOption>(account => ({
            key: account.path,
            text: account.name,
            data: { secondaryText: account.region.toUpperCase() },
            disabled: !allowedRegions.some(r => r === account.region)
        }));
};
